import { Zap } from "lucide-react";
import type { Team } from "@/lib/api/types";
import { useSetFastMode, useTeamMembers } from "@/hooks/queries/use-teams";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useAuthStore } from "@/stores/auth-store";

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

/**
 * Project setting: run builds in Claude Code fast mode by default (ENG-350).
 * Each run can still turn it on or off when it is sent. Project admins,
 * organization admins and platform admins can change it; everyone else sees it
 * read-only.
 */
export function FastModeCard({ team }: { team: Team }) {
  const me = useAuthStore((s) => s.user);
  const { data: onboarding } = useOnboardingState();
  const { data: members } = useTeamMembers(team.id);
  const setFastMode = useSetFastMode(team.id);

  const current = setFastMode.data?.data.fastMode ?? team.fastMode ?? false;
  const checked = setFastMode.isPending ? (setFastMode.variables ?? current) : current;
  const myRole = members?.find((m) => m.user.id === me?.id)?.role;
  const canEdit =
    !!me?.isGlobalAdmin ||
    (onboarding?.step === "ready" && onboarding.organizationRole === "admin") ||
    myRole === "admin";

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-medium">
          <Zap className="h-4 w-4" />
          Speed
        </h3>
        <p className="text-sm text-muted-foreground">
          The default for builds sent from this project. Each run can still change it.
        </p>
      </div>

      <label className="flex items-start gap-3 rounded-lg border border-white/[0.08] p-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={checked}
          disabled={!canEdit || setFastMode.isPending}
          onChange={(e) => { setFastMode.mutate(e.target.checked); }}
        />
        <span>
          <span className="block font-medium">Fast mode (faster output, higher cost)</span>
          <span className="block text-xs text-muted-foreground">
            Uses Claude Code&apos;s fast mode where the account has it; otherwise runs at standard speed.
          </span>
        </span>
      </label>

      {!canEdit ? (
        <p className="text-xs text-muted-foreground">Only a project admin can change this.</p>
      ) : null}
      {errorText(setFastMode.error) ? (
        <p className="text-xs text-destructive">{errorText(setFastMode.error)}</p>
      ) : null}
    </div>
  );
}
